<template>
  <b-container class="card-container">
    <b-card>
      <template v-slot:header>
        <span class="breadcrumbs"><b-link :to="{name: `${collection}s`}">Video Templates</b-link> / <b-link :to="{name: `${collection}`}">{{item ? item.name : ''}}</b-link> / Tune</span>
      </template>

      <Loading v-if="item === undefined"/>
      <Error v-else-if="item === null" code="404"></Error>
      <div v-else>
        <audio ref="audio" />
        <p>Select the settings which sound the best, click on each to listen (they may be different lengths).</p>

        <div v-for="(part, part_index) of item.parts" v-bind:key="`part-${part_index}`">
          <b-card v-if="part.type === 'variable'">

            <b-card-group deck>
              <b-card v-for="(settings_config, index) in part.group.settings_configs" v-bind:key="`part-${part_index}-${index}`" :header='`"${part.group.original.text}" - ${index+1}`' :bg-variant="part.group.settings_config_selected === index ? 'secondary' : 'light'" @click="select_settings_config(part_index, index)" class='clickable mb-4'>
                Click to play
              </b-card>
            </b-card-group>

            <h4>Test these settings</h4>

            <b-input-group>
              <b-input-group-append is-text>
                <b-form-input v-model="text" placeholder="Enter text" maxlength="30"></b-form-input>
              </b-input-group-append>

              <b-input-group-append is-text>
                Volume&nbsp;
                <b-form-input v-model='part.group.original.volume' min="0" max="3" step="0.1" placeholder="1.0" style="max-width: 100px"></b-form-input>
              </b-input-group-append>
              <b-input-group-append is-text>
                Speed&nbsp;
                <b-form-input v-model='part.group.original.speed' min="0" max="3" step="0.1" placeholder="1.0" style="max-width: 100px"></b-form-input>
              </b-input-group-append>
              <b-input-group-append is-text>
                Pitch&nbsp;
                <b-form-input v-model='part.group.original.pitch' min="0.5" max="1.5" step="0.02" placeholder="1.0" style="max-width: 100px"></b-form-input>
              </b-input-group-append>
              <b-input-group-append is-text>
                <LoadingButton :loading="audio_loading[part_index]" @click="text_to_speech(part_index, part)" variant='primary'>Generate</LoadingButton>
              </b-input-group-append>
            </b-input-group>

            <Audio v-if="audio_url[part_index]" :source="audio_url[part_index]" :autoplay="true" />
          </b-card>
        </div>

        <p>None of these sound good? You may need to redo the video, <a href='#' @click="open_chat">contact support</a> and our team can help</p>

        <LoadingButton @click="save" :loading="is_saving" variant="primary">Save</LoadingButton>
      </div>
    </b-card>
  </b-container>
</template>

<script>
import { db, config } from '@/main.js'
import api from '@/lib/api.js'
import Audio from '@/components/Audio.vue'

export default {
  metaInfo: {title: 'Video Tune'},
  props: ['from_item'],
  components: {Audio},
  firestore: function () {
    return {
      item: db.collection(this.collection).doc(this.$route.params.id),
    }
  },
  data() {
    return {
      collection: 'project',
      item: this.from_item,
      is_saving: false,
      audio_loading: {},
      text: null,
      audio_url: {}
    }
  },
  methods: {
    select_settings_config(part_index, index) {
      this.item.parts[part_index].group.settings_config_selected = index
      let variant = this.item.parts[part_index].group.settings_configs[index]

      this.$refs.audio.src = variant.audio_url
      this.$refs.audio.play()
      this.$forceUpdate()

      // Update the settings
      if (variant.prefix) this.item.parts[part_index].group.prefix.enabled = variant.prefix.enabled
      if (variant.original) this.item.parts[part_index].group.original.enabled = variant.original.enabled
      if (variant.suffix) this.item.parts[part_index].group.suffix.enabled = variant.suffix.enabled
    },
    open_chat() {
      window.HubSpotConversations.widget.open()
    },
    async save() {
      this.is_saving = true
      let updates = {
        updated: new Date(),
        parts: this.item.parts,
        dismiss_tune: true,
      }

      await this.$firestoreRefs.item.update(updates)
      this.is_saving = false
      this.$router.push({name: this.collection})
    },
    async text_to_speech(part_index, part) {
      this.audio_url[part_index] = null

      let prefix_text, prefix_url, original_text, original_url, suffix_text, suffix_url, volume, speed, pitch
      if (part.group.prefix && part.group.prefix.enabled) {
        prefix_text = part.group.prefix.text
        prefix_url = part.group.prefix.audio_url
      }
      if (part.group.original && part.group.original.enabled) {
        original_text = part.group.original.text
        original_url = part.group.original.audio_url
      }
      if (part.group.suffix && part.group.suffix.enabled) {
        suffix_text = part.group.suffix.text
        suffix_url = part.group.suffix.audio_url
      }

      volume = Number(part.group.original.volume || 1.0)
      speed = Number(part.group.original.speed || 1.0)
      pitch = Number(part.group.original.pitch || 1.0)

      // console.log(prefix_text, prefix_url, original_text, original_url, suffix_text, suffix_url, volume, speed, pitch)

      // TODO
      if (this.text.length > 60 || !this.text) {
        this.$swal({title: 'Enter one word', text: "Please enter between 0 and 60 characters", icon: 'warning'})
        return
      }

      this.audio_loading[part_index] = true
      this.$forceUpdate()

      let response
      try {
        response = await api.text_to_speech(this.text, this.item.voice_doc.id, prefix_text, prefix_url, original_text, original_url, suffix_text, suffix_url, volume, speed, pitch)
      } catch (err) {
        this.audio_loading[part_index] = false
        this.$forceUpdate()
        console.warn(err)
        this.$swal({title: 'Uh oh', text: 'Something went wrong generating audio, please contact support', icon: 'warning'})
        return
      }
      this.audio_loading[part_index] = false
      this.$forceUpdate()

      if (response.data.error) {
        this.$swal({title: response.data.error, text: response.data.message, icon: 'warning'})
        return
      }

      this.audio_loading[part_index] = false
      this.audio_url[part_index] = response.data.url

      this.$forceUpdate()

      return response.data.url
    },
  }
}
</script>

<style>

</style>
